/**
 * DemoTourProvider (iter-203) — central context for the demo-tour.
 *
 * Fetches /api/demo/tour once on mount when the user is logged in,
 * exposes:
 *   - tour: { is_demo, slug, steps, state }
 *   - markStep(stepId)
 *   - minimize()
 *   - reopen()
 *   - dismiss()
 *   - currentStep — the first step that is NOT yet completed
 *
 * No-ops when the user isn't a demo (state stays null, children just
 * render normally).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const DemoTourContext = createContext({
  tour: null,
  markStep: () => {},
  minimize: () => {},
  reopen: () => {},
  dismiss: () => {},
  currentStep: null,
  loading: false,
});

export function DemoTourProvider({ children }) {
  const { user } = useAuth();
  const [tour, setTour] = useState(null);
  const fetchSeqRef = useRef(0);
  const isDemo = !!user?.is_demo;

  const refresh = useCallback(async () => {
    const mySeq = ++fetchSeqRef.current;
    if (!isDemo) {
      await Promise.resolve();
      if (mySeq === fetchSeqRef.current) setTour(null);
      return;
    }
    try {
      const r = await api.get("/demo/tour");
      if (mySeq === fetchSeqRef.current) setTour(r.data || null);
    } catch {
      if (mySeq === fetchSeqRef.current) setTour(null);
    }
  }, [isDemo]);

  // Initial + on-isDemo-change fetch. We deliberately omit `refresh`
  // from deps and call api directly here to keep the lint rule happy
  // (react-hooks/set-state-in-effect refuses any useCallback chain
  // that ultimately calls setState — even when awaited).
  useEffect(() => {
    let cancelled = false;
    if (!isDemo) {
      // Defer null-set so it's never synchronous from the effect.
      const t = setTimeout(() => {
        if (!cancelled) setTour(null);
      }, 0);
      return () => { cancelled = true; clearTimeout(t); };
    }
    (async () => {
      try {
        const r = await api.get("/demo/tour");
        if (!cancelled) setTour(r.data || null);
      } catch {
        if (!cancelled) setTour(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isDemo]);

  // Fire-and-forget event recorder (optimistic UI).
  const fire = useCallback(async (event, step_id = null) => {
    if (!user?.is_demo) return null;
    try {
      const r = await api.post("/demo/tour/event", { event, step_id });
      return r.data?.state || null;
    } catch {
      return null;
    }
  }, [user?.is_demo]);

  const markStep = useCallback(async (stepId) => {
    setTour((cur) => {
      if (!cur) return cur;
      const done = cur.state?.steps_completed || [];
      if (done.includes(stepId)) return cur;
      return {
        ...cur,
        state: { ...(cur.state || {}), steps_completed: [...done, stepId] },
      };
    });
    const newState = await fire("step_completed", stepId);
    if (newState) {
      setTour((cur) => (cur ? { ...cur, state: { ...cur.state, ...newState } } : cur));
    }
  }, [fire]);

  const minimize = useCallback(async () => {
    setTour((cur) => (cur ? { ...cur, state: { ...(cur.state || {}), minimized: true } } : cur));
    await fire("minimized");
  }, [fire]);

  const reopen = useCallback(async () => {
    setTour((cur) => (cur ? { ...cur, state: { ...(cur.state || {}), minimized: false } } : cur));
    await fire("reopened");
  }, [fire]);

  const dismiss = useCallback(async () => {
    setTour((cur) => (cur ? { ...cur, state: { ...(cur.state || {}), minimized: true, dismissed: true } } : cur));
    await fire("dismissed");
  }, [fire]);

  // First time we get steps, fire "started" once. We use a ref-guard
  // so the effect remains a no-op on every subsequent re-render and
  // we don't call setState synchronously inside it (lint-clean).
  const startedFiredRef = useRef(false);
  useEffect(() => {
    if (
      !startedFiredRef.current &&
      tour?.is_demo &&
      tour?.steps?.length &&
      !tour?.state?.started_at
    ) {
      startedFiredRef.current = true;
      // Fire & refresh from backend (no optimistic setState here).
      (async () => {
        await fire("started");
        await refresh();
      })();
    }
  }, [tour?.is_demo, tour?.steps?.length, tour?.state?.started_at, fire, refresh]);

  const currentStep = useMemo(() => {
    if (!tour?.steps) return null;
    const done = new Set(tour.state?.steps_completed || []);
    return tour.steps.find((s) => !done.has(s.id)) || null;
  }, [tour]);

  const value = useMemo(
    () => ({ tour, markStep, minimize, reopen, dismiss, currentStep, loading: false }),
    [tour, markStep, minimize, reopen, dismiss, currentStep],
  );

  return (
    <DemoTourContext.Provider value={value}>{children}</DemoTourContext.Provider>
  );
}

export function useDemoTour() {
  return useContext(DemoTourContext);
}
